import type { World2DCamera } from '../camera';
import { getGrid } from '../../../grid';
import { resolveTileDrawMetrics } from '../../tilemap/board';

interface FogLayerState {
  readonly visibilityMode?: 'radius' | 'full_map';
  readonly selfPosition: { x: number; y: number } | null;
  readonly activeRadius: number;
  readonly mapWidth: number;
  readonly mapHeight: number;
  readonly gridType: string;
}

interface DrawClipRect {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

export class FogLayer {
  public draw(
    ctx: CanvasRenderingContext2D,
    camera: World2DCamera,
    state: FogLayerState,
    overscanPx = 0,
    clipRect: DrawClipRect | null = null,
    screenOffsetX = 0,
    screenOffsetY = 0,
  ): void {
    if (state.visibilityMode === 'full_map' || !state.selfPosition || state.activeRadius <= 0) {
      return;
    }

    const grid = getGrid(state.gridType);
    const tileSize = camera.getPixelsPerWorldUnit();
    const visibleBounds = resolveVisibleTileBounds(camera, grid, state.gridType, state.mapWidth, state.mapHeight, overscanPx);

    ctx.save();
    if (clipRect) {
      ctx.beginPath();
      ctx.rect(clipRect.x, clipRect.y, clipRect.width, clipRect.height);
      ctx.clip();
    }
    if (screenOffsetX !== 0 || screenOffsetY !== 0) {
      ctx.translate(screenOffsetX, screenOffsetY);
    }
    ctx.fillStyle = 'rgba(10, 12, 18, 0.58)';

    for (let y = visibleBounds.minY; y <= visibleBounds.maxY; y += 1) {
      for (let x = visibleBounds.minX; x <= visibleBounds.maxX; x += 1) {
        if (grid.distance(state.selfPosition.x, state.selfPosition.y, x, y) <= state.activeRadius) {
          continue;
        }
        const metrics = resolveTileDrawMetrics(grid, tileSize, x, y);
        const center = camera.worldPointToScreen({ x: metrics.centerX, y: metrics.centerY }, grid);
        grid.fillTile(ctx, center.x, center.y, tileSize * 0.5, x, y);
      }
    }

    ctx.restore();
  }
}

interface TileBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function resolveVisibleTileBounds(
  camera: World2DCamera,
  grid: ReturnType<typeof getGrid>,
  gridType: string,
  mapWidth: number,
  mapHeight: number,
  overscanPx = 0,
): TileBounds {
  const viewport = camera.getViewportSize();
  const marginPx = camera.getPixelsPerWorldUnit() * 2 + overscanPx;
  const samplePoints = [
    { x: -marginPx, y: -marginPx },
    { x: viewport.width / 2, y: -marginPx },
    { x: viewport.width + marginPx, y: -marginPx },
    { x: -marginPx, y: viewport.height / 2 },
    { x: viewport.width / 2, y: viewport.height / 2 },
    { x: viewport.width + marginPx, y: viewport.height / 2 },
    { x: -marginPx, y: viewport.height + marginPx },
    { x: viewport.width / 2, y: viewport.height + marginPx },
    { x: viewport.width + marginPx, y: viewport.height + marginPx },
  ];

  let minX = Number.POSITIVE_INFINITY;
  let minY = Number.POSITIVE_INFINITY;
  let maxX = Number.NEGATIVE_INFINITY;
  let maxY = Number.NEGATIVE_INFINITY;

  for (const point of samplePoints) {
    const world = camera.screenToWorld(point, grid);
    const tile = grid.pixelToTile(world.x, world.y, camera.getPixelsPerWorldUnit());
    minX = Math.min(minX, tile.q);
    minY = Math.min(minY, tile.r);
    maxX = Math.max(maxX, tile.q);
    maxY = Math.max(maxY, tile.r);
  }

  const marginTiles = gridType === 'hex' ? 3 : 2;
  return {
    minX: Math.max(0, Math.floor(minX) - marginTiles),
    minY: Math.max(0, Math.floor(minY) - marginTiles),
    maxX: Math.min(mapWidth - 1, Math.ceil(maxX) + marginTiles),
    maxY: Math.min(mapHeight - 1, Math.ceil(maxY) + marginTiles),
  };
}
